import { useCallback, useEffect, type ComponentType } from 'react';
import styled from '@emotion/styled';
import { useSetAtom } from 'jotai';
import { useRouter } from 'next/router';
import appConfig from 'app-config';
import Header from 'src/components/Header';
import LoginModal from 'src/components/LoginModal';
import useLogin from 'src/hooks/useLogin';
import { Routes } from 'src/router/route';
import { showModalAtom } from 'src/stores/modal/atoms';
import { ModalType } from 'src/types/Modal';
const DesktopOnlyHeader = styled(Header)`
  ${({
  theme
}) => theme.screenSize.tablet} {
    display: none;
  }
`;

/**
 * @description
 * 페이지 컴포넌트를 인증된 사용자만 접근할 수 있도록 래핑하는 고차 컴포넌트입니다.
 * 로그인을 하지 않았을 경우 로그인 모달을 표시하고 여기서 로그인 성공 시 미러 화면으로 리다이렉트합니다.
 * appConfig 설정에 따라 모달 표시 여부를 결정할 수 있습니다.
 *
 * @template P - 페이지 컴포넌트의 props 타입
 * @param PageComponent - 보호할 페이지 컴포넌트
 * @returns 인증 로직이 추가된 새로운 컴포넌트
 */
const withPrivateRoute = <P extends Record<string, unknown>,>(PageComponent: ComponentType<P>) => {
  const WithPrivateRoute = (props: P) => {
    const router = useRouter();
    const {
      isLogin
    } = useLogin();
    const showModal = useSetAtom(showModalAtom);
    const loginSuccessCallback = useCallback(() => {
      router.push({
        ...Routes['/']()
      });
    }, [router]);
    useEffect(() => {
      if (!isLogin) {
        showModal({
          key: ModalType.LOGIN,
          component: () => <LoginModal loginSuccessCallback={loginSuccessCallback} closeWithESC={false} closeWhenClickOutside={false} />
        });
      }
    }, [isLogin, showModal, loginSuccessCallback]);
    if (!isLogin) return <>
          <DesktopOnlyHeader />
        </>;
    return <PageComponent {...props} data-sentry-element="PageComponent" data-sentry-component="WithPrivateRoute" data-sentry-source-file="index.tsx" />;
  };
  return appConfig.layout.showLoginModalOnPrivateRoute ? WithPrivateRoute : PageComponent;
};
export default withPrivateRoute;